import { bozpTrainingControllerRecordEventV1 } from '@fixit/api-client/bozp-training';
// Import the single model file, NOT the `@fixit/api-client/models` barrel — the barrel
// re-exports ~1600 generated files and Vite dev-serves every one as its own request.
import { RecordEventDtoType } from '@fixit/api-client/models/recordEventDtoType';

/** Frozen event enum — mirrors RecordEventDtoType / bozp_training_events.type. */
export type BozpEventType =
  | 'started'
  | 'chapter_viewed'
  | 'section_viewed'
  | 'widget_interacted'
  | 'answer_submitted'
  | 'passed'
  | 'failed'
  | 'abandoned';

/**
 * Frozen event enum → xAPI verb (design §11).
 * source of truth: Plan 5 bozp telemetry EVENT_VERB_MAP.
 * Mirrored here verbatim because the api package does not export it to the player bundle;
 * if Plan 5 ships an exported `EVENT_VERB_MAP` from `@fixit/api-client` (or a shared enum
 * package), import it instead of this local copy. Do NOT diverge from Plan 5's mapping.
 */
const VERB: Record<BozpEventType, string> = {
  started: 'launched',
  chapter_viewed: 'initialized',
  section_viewed: 'experienced',
  widget_interacted: 'interacted',
  answer_submitted: 'answered',
  passed: 'passed',
  failed: 'failed',
  abandoned: 'abandoned',
};

interface QueuedEvent {
  hazardProfileId: string;
  sessionId: string;
  type: BozpEventType;
  payload: Record<string, unknown>;
}

export interface TelemetryContext {
  hazardProfileId: string;
  sessionId: string;
  /** versionHash of the training content the worker is taking. */
  contentVersion: string;
  schemaVersion: number;
}

export interface TelemetryQueue {
  emit(type: BozpEventType, extra?: Record<string, unknown>): Promise<boolean>;
  flush(): Promise<void>;
  pending(): number;
  failure(): 'retry' | 'restart' | 'reauthenticate' | null;
}

/**
 * Never-throwing telemetry. emit() builds an xAPI-vocabulary payload,
 * tries to POST it, and on failure keeps it queued for the next flush(). The worker
 * waits for delivery of required chapter evidence before progressing. emit returns
 * whether its event was acknowledged; failed events remain queued for retry.
 */
export function createTelemetryQueue(ctx: TelemetryContext): TelemetryQueue {
  const queue: QueuedEvent[] = [];
  let inFlight: Promise<void> | null = null;
  let failure: ReturnType<TelemetryQueue['failure']> = null;

  async function post(ev: QueuedEvent): Promise<boolean> {
    try {
      await bozpTrainingControllerRecordEventV1({
        hazardProfileId: ev.hazardProfileId,
        sessionId: ev.sessionId,
        // Coerce the public string-union into the generated TS enum the DTO field demands.
        type: RecordEventDtoType[ev.type],
        payload: ev.payload,
      });
      failure = null;
      return true;
    } catch (error) {
      const code = error && typeof error === 'object' && 'code' in error ? error.code : null;
      const status =
        error && typeof error === 'object' && 'statusCode' in error ? error.statusCode : null;
      failure =
        status === 401 ? 'reauthenticate' : code === 'bozp.session.invalid' ? 'restart' : 'retry';
      // Preserve the rejected head and its recovery action; a new session gets a new queue.
      return false;
    }
  }

  async function drain(): Promise<void> {
    try {
      while (queue.length > 0) {
        const ok = await post(queue[0]);
        if (!ok) return; // stop on first failure; preserve order for the next flush
        queue.shift();
      }
    } finally {
      inFlight = null;
    }
  }

  function flush(): Promise<void> {
    // All emitters share one drain: an acknowledgment must only remove the
    // event it posted, never the next event another drain is still sending.
    if (inFlight) return inFlight;
    if (queue.length === 0) return Promise.resolve();
    inFlight = drain();
    return inFlight;
  }

  async function emit(type: BozpEventType, extra: Record<string, unknown> = {}): Promise<boolean> {
    const ev: QueuedEvent = {
      hazardProfileId: ctx.hazardProfileId,
      sessionId: ctx.sessionId,
      type,
      payload: {
        verb: VERB[type],
        contentVersion: ctx.contentVersion,
        schemaVersion: ctx.schemaVersion,
        ...extra,
      },
    };
    queue.push(ev);
    await flush();
    return !queue.includes(ev);
  }

  return { emit, flush, pending: () => queue.length, failure: () => failure };
}

/**
 * Wire a best-effort 'abandoned' event on tab close / hidden. Fires an in-flight `fetch`
 * with `keepalive: true` so the request survives unload while still carrying a real
 * `Authorization: Bearer` header — the scoped JWT is NEVER put in a URL/query (no sendBeacon
 * with `?bt=`, which would leak the token into logs/history). Returns a cleanup fn.
 */
export function installAbandonOnUnload(
  ctx: TelemetryContext,
  apiBaseUrl: string,
  getToken: () => string | null,
): () => void {
  let sent = false;
  const handler = () => {
    if (sent) return; // visibilitychange + pagehide can both fire; send once
    const token = getToken();
    if (!token) return;
    sent = true;
    const body = JSON.stringify({
      hazardProfileId: ctx.hazardProfileId,
      sessionId: ctx.sessionId,
      type: 'abandoned',
      payload: {
        verb: 'abandoned',
        contentVersion: ctx.contentVersion,
        schemaVersion: ctx.schemaVersion,
      },
    });
    // keepalive lets the browser flush this request after the page is gone; the bearer
    // rides in the Authorization header (never in the URL). Best-effort: swallow rejection.
    void fetch(`${apiBaseUrl}/api/v1/bozp/events`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body,
    }).catch(() => {
      // Unload-time transport failure (offline / aborted). 'abandoned' is supplementary
      // audit color, never load-bearing — nothing to recover here.
    });
  };
  // Named wrapper so the cleanup below detaches the SAME reference we added
  // (removeEventListener with a fresh arrow would be a silent no-op → listener leak).
  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') handler();
  };
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', handler);
  return () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', handler);
  };
}
